import { useParams, Link } from "react-router-dom";
import { useMemo } from "react";
import SEO from "@/components/base/SEO";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const article = useMemo(() => articles.find((a) => a.id === id), [id]);
  const category = useMemo(
    () => (article ? categories.find((c) => c.slug === article.category) : null),
    [article]
  );
  const relatedArticles = useMemo(
    () =>
      article
        ? articles
            .filter((a) => a.category === article.category && a.id !== article.id)
            .slice(0, 3)
        : [],
    [article]
  );

  const formatViews = (views: number) => {
    if (views >= 10000) {
      return `${(views / 10000).toFixed(1)}万`;
    }
    return views.toString();
  };

  if (!article) {
    return (
      <div className="min-h-screen bg-background-950 flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-200 mb-2">文章不存在</h1>
            <Link to="/" className="text-primary-400 hover:text-primary-300 transition-colors cursor-pointer text-sm">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    author: {
      "@type": "Organization",
      name: article.author,
    },
    publisher: {
      "@type": "Organization",
      name: "黑料王",
      url: "https://heiliaowang.com",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://heiliaowang.com/article/${article.id}`,
    },
  };

  const breadcrumbItems = [
    { label: "首页", href: "/" },
    ...(category ? [{ label: category.name, href: `/category/${category.slug}` }] : []),
    { label: article.title },
  ];

  return (
    <>
      <SEO
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(",")}
        canonicalUrl={`https://heiliaowang.com/article/${article.id}`}
        ogImage={article.imageUrl}
        ogType="article"
        schema={articleSchema}
      />

      <div className="min-h-screen bg-background-950 flex flex-col">
        <Navbar />
        <main className="flex-1">
          <article className="w-full px-4 md:px-6 lg:px-8 max-w-4xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />

            <header className="py-4 md:py-6">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs text-accent-400 bg-accent-500/10 px-3 py-1 rounded-full whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
                {article.isHot && (
                  <span className="text-xs text-primary-400 bg-primary-500/10 px-3 py-1 rounded-full whitespace-nowrap flex items-center gap-1">
                    <i className="ri-fire-line text-[10px]"></i>热门
                  </span>
                )}
              </div>
              <h1 className="text-xl md:text-3xl font-heading font-bold text-foreground-100 leading-snug">
                {article.title}
              </h1>
              <div className="mt-4 flex items-center gap-3 text-sm text-foreground-500 flex-wrap">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="w-1 h-1 bg-foreground-700 rounded-full"></span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.publishDate}
                </span>
                <span className="w-1 h-1 bg-foreground-700 rounded-full"></span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {formatViews(article.views)} 阅读
                </span>
              </div>
            </header>

            <div className="mb-6 rounded-lg overflow-hidden aspect-[16/9]">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={`${article.title} - 黑料王`}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-invert max-w-none pb-8 article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="border-t border-background-800/60 pt-6">
              <p className="text-xs text-foreground-600 mb-1">声明：</p>
              <p className="text-xs text-foreground-600 leading-relaxed">
                本文内容由黑料王编辑部整理发布，仅供娱乐参考。如涉及侵权或不实内容，请及时与我们联系，我们将在第一时间核实处理。未经授权，禁止转载。
              </p>
            </div>

            {category && (
              <div className="mt-6 pb-8">
                <Link
                  to={`/category/${category.slug}`}
                  className="inline-flex items-center gap-2 text-sm text-primary-400 hover:text-primary-300 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回「{category.name}」分类
                </Link>
              </div>
            )}

            {relatedArticles.length > 0 && (
              <section className="border-t border-background-800/60 py-8" aria-label="相关文章">
                <h2 className="text-lg font-heading font-bold text-foreground-100 mb-4">
                  相关爆料
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <ArticleCard key={ra.id} article={ra} variant="default" />
                  ))}
                </div>
              </section>
            )}
          </article>
        </main>
        <Footer />
      </div>

      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
    </>
  );
}